import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, UserCog, Layers, History, Wand2 } from "lucide-react";

const ROLE_LABEL = { admin: "Titolare", ufficio: "Ufficio", montatore: "Montatore" };

export default function Accessi() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState([]);
  const [roles, setRoles] = useState([]);
  const [edit, setEdit] = useState(null); // user being edited (or new)
  const [form, setForm] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [audit, setAudit] = useState([]);
  const [auditQ, setAuditQ] = useState("");
  const [auditFrom, setAuditFrom] = useState("");
  const [auditTo, setAuditTo] = useState("");
  const [tab, setTab] = useState("utenti");
  const [editProfile, setEditProfile] = useState(null);
  const [pForm, setPForm] = useState(null);

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data.users); setMeta(data.permission_meta); setRoles(data.roles);
  };
  const loadProfiles = async () => { const { data } = await api.get("/permission-profiles"); setProfiles(data); };
  const loadAudit = async () => {
    const p = new URLSearchParams();
    if (auditQ) p.set("q", auditQ);
    if (auditFrom) p.set("date_from", auditFrom);
    if (auditTo) p.set("date_to", auditTo);
    const { data } = await api.get(`/audit-log?${p.toString()}`);
    setAudit(data);
  };
  useEffect(() => { load(); loadProfiles(); }, []);
  useEffect(() => { const t = setTimeout(loadAudit, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [auditQ, auditFrom, auditTo]);

  const applyProfile = (prof) => {
    setForm((f) => ({ ...f, permissions: { ...(prof.permissions || {}) } }));
    toast.success(`Profilo "${prof.name}" applicato — ricorda di salvare`);
  };
  const openNewProfile = () => { setEditProfile("new"); setPForm({ name: "", permissions: {} }); };
  const openEditProfile = (p) => { setEditProfile(p.id); setPForm({ ...p, permissions: { ...(p.permissions || {}) } }); };
  const toggleProfilePerm = (key, val) => setPForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: val } }));
  const saveProfile = async () => {
    try {
      if (!pForm.name) return toast.error("Nome profilo obbligatorio");
      if (editProfile === "new") await api.post("/permission-profiles", pForm);
      else await api.put(`/permission-profiles/${editProfile}`, pForm);
      toast.success("Profilo salvato"); setEditProfile(null); setPForm(null); loadProfiles(); loadAudit();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const delProfile = async (p) => {
    if (!window.confirm(`Eliminare il profilo "${p.name}"?`)) return;
    await api.delete(`/permission-profiles/${p.id}`); toast.success("Profilo eliminato"); loadProfiles(); loadAudit();
  };

  const groups = [...new Set(meta.map((m) => m.group))];

  const openNew = () => { setEdit("new"); setForm({ role: "montatore", permissions: {}, active: true }); };
  const openEdit = (u) => { setEdit(u.user_id); setForm({ ...u, permissions: { ...(u.permissions || {}) }, password: "" }); };

  const effFor = (key) => {
    // show effective value considering role default + override for the form
    const u = { role: form.role, permissions: form.permissions };
    if (form.permissions[key] !== undefined) return form.permissions[key];
    // role defaults (mirror backend): admin all true; ufficio all true except manage_users; montatore all false
    if (form.role === "admin") return true;
    if (form.role === "ufficio") return key !== "manage_users";
    return false;
  };

  const togglePerm = (key, val) => {
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: val } }));
  };
  const resetPerm = (key) => {
    setForm((f) => { const p = { ...f.permissions }; delete p[key]; return { ...f, permissions: p }; });
  };

  const save = async () => {
    try {
      if (edit === "new") {
        if (!form.email || !form.password) return toast.error("Email e password obbligatorie");
        await api.post("/users", form);
        toast.success("Accesso creato");
      } else {
        await api.put(`/users/${edit}`, form);
        toast.success("Permessi aggiornati");
      }
      setEdit(null); setForm(null); load(); loadAudit();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const del = async (u) => {
    if (!window.confirm(`Eliminare l'accesso di ${u.name}?`)) return;
    try { await api.delete(`/users/${u.user_id}`); toast.success("Accesso eliminato"); load(); loadAudit(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div>
      <PageHeader title="Accessi e permessi" subtitle="Ruoli come base, profili riutilizzabili, permessi per persona e registro attività">
        {tab === "utenti" && <Button className="bg-brand hover:bg-[#A8472F] text-white" data-testid="btn-new-user" onClick={openNew}><Plus size={16} className="mr-1" /> Nuovo accesso</Button>}
        {tab === "profili" && <Button className="bg-brand hover:bg-[#A8472F] text-white" data-testid="btn-new-profile" onClick={openNewProfile}><Plus size={16} className="mr-1" /> Nuovo profilo</Button>}
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="utenti" data-testid="tab-utenti">Utenti ({users.length})</TabsTrigger>
          <TabsTrigger value="profili" data-testid="tab-profili">Profili ({profiles.length})</TabsTrigger>
          <TabsTrigger value="registro" data-testid="tab-registro">Registro</TabsTrigger>
        </TabsList>

        <TabsContent value="utenti">
      {users.length === 0 ? <EmptyState title="Nessun accesso" /> : (
        <div className="space-y-2">
          {users.map((u) => {
            const eff = u.effective_perms || {};
            const activeCount = Object.values(eff).filter(Boolean).length;
            return (
              <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="card-soft p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div className="min-w-0">
                  <p className="font-head font-bold flex items-center gap-2">
                    {u.name}
                    <span className="text-xs px-2 py-0.5 rounded-full bg-accent capitalize">{ROLE_LABEL[u.role] || u.role}</span>
                    {!u.active && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">disattivato</span>}
                  </p>
                  <p className="text-sm text-muted-foreground">{u.email}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{activeCount} permessi attivi{Object.keys(u.permissions || {}).length ? ` · ${Object.keys(u.permissions).length} personalizzati` : ""}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" data-testid={`edit-user-${u.user_id}`} onClick={() => openEdit(u)}><UserCog size={14} className="mr-1" /> Permessi</Button>
                  {u.user_id !== me?.user_id && u.email !== "eliafiorenzani@gmail.com" && (
                    <button onClick={() => del(u)} className="text-destructive px-2" data-testid={`del-user-${u.user_id}`}><Trash2 size={16} /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
        </TabsContent>

        <TabsContent value="profili">
          {profiles.length === 0 ? <EmptyState title="Nessun profilo" hint="Crea profili riutilizzabili da applicare agli accessi con un clic." /> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {profiles.map((p) => {
                const n = Object.values(p.permissions || {}).filter(Boolean).length;
                return (
                  <div key={p.id} data-testid={`profile-${p.id}`} className="card-soft p-4">
                    <div className="flex items-center justify-between">
                      <p className="font-head font-bold flex items-center gap-2"><Layers size={16} className="text-brand" /> {p.name}</p>
                      <div className="flex gap-1">
                        <button onClick={() => openEditProfile(p)} className="text-muted-foreground hover:text-brand" data-testid={`edit-profile-${p.id}`}><UserCog size={16} /></button>
                        <button onClick={() => delProfile(p)} data-testid={`del-profile-${p.id}`} className="text-destructive"><Trash2 size={16} /></button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{n} permessi attivi</p>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="registro">
          <div className="flex flex-wrap gap-2 items-end mb-4">
            <div className="flex-1 min-w-[180px]"><Label className="text-xs">Cerca (persona, azione, dettaglio)</Label><Input data-testid="audit-search" value={auditQ} onChange={(e) => setAuditQ(e.target.value)} placeholder="es. Marco, permessi, profilo…" /></div>
            <div><Label className="text-xs">Dal</Label><Input type="date" data-testid="audit-from" value={auditFrom} onChange={(e) => setAuditFrom(e.target.value)} /></div>
            <div><Label className="text-xs">Al</Label><Input type="date" data-testid="audit-to" value={auditTo} onChange={(e) => setAuditTo(e.target.value)} /></div>
            {(auditQ || auditFrom || auditTo) && <Button variant="ghost" size="sm" data-testid="audit-clear" onClick={() => { setAuditQ(""); setAuditFrom(""); setAuditTo(""); }}>Azzera</Button>}
          </div>
          {audit.length === 0 ? <EmptyState title="Nessuna attività trovata" /> : (
            <div className="space-y-2">
              {audit.map((a) => (
                <div key={a.id} data-testid={`audit-${a.id}`} className="card-soft p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="font-medium text-sm"><History size={13} className="inline mr-1 text-brand" />{a.action}{a.target_name ? ` · ${a.target_name}` : ""}</p>
                    <span className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString("it-IT")}</span>
                  </div>
                  {a.detail && <p className="text-xs text-muted-foreground mt-1">{a.detail}</p>}
                  <p className="text-[11px] text-muted-foreground mt-0.5">da {a.actor_name}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!edit} onOpenChange={(v) => { if (!v) { setEdit(null); setForm(null); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><ShieldCheck size={18} className="text-brand" /> {edit === "new" ? "Nuovo accesso" : "Permessi di " + (form?.name || "")}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              {edit === "new" && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Nome</Label><Input data-testid="user-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>Email *</Label><Input data-testid="user-email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div><Label>Password *</Label><Input data-testid="user-password" type="text" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Ruolo (base)</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {edit !== "new" && (
                  <label className="flex items-center gap-2 text-sm mt-6"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-active" /> Account attivo</label>
                )}
              </div>
              {edit !== "new" && (
                <div><Label>Nuova password (lascia vuoto per non cambiare)</Label><Input type="text" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              )}

              {form.role !== "admin" && profiles.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap bg-accent rounded-lg p-3">
                  <Wand2 size={16} className="text-brand" />
                  <Label className="shrink-0">Applica un profilo:</Label>
                  <Select onValueChange={(v) => { const p = profiles.find((x) => x.id === v); if (p) applyProfile(p); }}>
                    <SelectTrigger data-testid="apply-profile" className="max-w-xs"><SelectValue placeholder="Scegli un profilo…" /></SelectTrigger>
                    <SelectContent>{profiles.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}

              {form.role === "admin" ? (
                <p className="text-sm bg-accent rounded-lg p-3">Il Titolare (admin) ha sempre tutti i permessi.</p>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">Attiva o disattiva nel dettaglio. Il colore indica il valore effettivo; "da ruolo" significa che segue l'impostazione del ruolo.</p>
                  {groups.map((g) => (
                    <div key={g}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-brand mb-1">{g}</p>
                      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                        {meta.filter((m) => m.group === g).map((m) => {
                          const overridden = form.permissions[m.key] !== undefined;
                          return (
                            <div key={m.key} className="flex items-center justify-between py-1.5 border-b border-border/60">
                              <span className="text-sm">{m.label}</span>
                              <div className="flex items-center gap-2">
                                {overridden && <button onClick={() => resetPerm(m.key)} className="text-[10px] text-muted-foreground underline">da ruolo</button>}
                                {!overridden && <span className="text-[10px] text-muted-foreground">da ruolo</span>}
                                <Switch data-testid={`perm-${m.key}`} checked={effFor(m.key)} onCheckedChange={(v) => togglePerm(m.key, v)} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => { setEdit(null); setForm(null); }}>Annulla</Button><Button className="bg-brand text-white" data-testid="user-save" onClick={save}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile management dialog */}
      <Dialog open={!!editProfile} onOpenChange={(v) => { if (!v) { setEditProfile(null); setPForm(null); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="profile-dialog">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Layers size={18} className="text-brand" /> {editProfile === "new" ? "Nuovo profilo" : "Modifica profilo"}</DialogTitle></DialogHeader>
          {pForm && (
            <div className="space-y-4">
              <div><Label>Nome profilo *</Label><Input data-testid="profile-name" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} placeholder="es. Capo squadra" /></div>
              {[...new Set(meta.map((m) => m.group))].map((g) => (
                <div key={g}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand mb-1">{g}</p>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                    {meta.filter((m) => m.group === g).map((m) => (
                      <div key={m.key} className="flex items-center justify-between py-1.5 border-b border-border/60">
                        <span className="text-sm">{m.label}</span>
                        <Switch data-testid={`profile-perm-${m.key}`} checked={!!pForm.permissions[m.key]} onCheckedChange={(v) => toggleProfilePerm(m.key, v)} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => { setEditProfile(null); setPForm(null); }}>Annulla</Button><Button className="bg-brand text-white" data-testid="profile-save" onClick={saveProfile}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
